import type {
  RosterTemplateCatalogEntry,
  RosterTemplatesResponse,
} from './rosterTemplateTypes';

/**
 * Built-in FEMA EOC chart. Shown only while FEMA daily operations briefing is
 * on.
 */
export const FEMA_EOC_TEMPLATE_SLUG = 'fema-eoc';

/**
 * Seats that exist only on the FEMA EOC chart. Standard ICS titles stay
 * addable.
 */
export const FEMA_EOC_CUSTOM_TITLES = [
  'EOC Director',
  'Situational Awareness Section',
  'Planning Support Section',
  'Resources Support Section',
  'Center Support Section',
] as const;

/** The badge the API stamps on templates that came from the org's saved library. */
export const ORG_TEMPLATE_BADGE_CATEGORY = 'organization';

export function isOrgTemplate(entry: RosterTemplateCatalogEntry): boolean {
  return entry.category === ORG_TEMPLATE_BADGE_CATEGORY;
}

const ORG_TEMPLATE_SLUG_PREFIX = 'org:';

/**
 * The library row id behind an org template's catalog slug, or null for a
 * built-in.
 */
export function orgTemplateIdFromSlug(slug: string): string | null {
  if (!slug.startsWith(ORG_TEMPLATE_SLUG_PREFIX)) return null;
  return slug.slice(ORG_TEMPLATE_SLUG_PREFIX.length).trim() || null;
}

/** Split API catalog entries into Load templates vs attach-only expansions. */
export function partitionRosterTemplates(
  entries: RosterTemplateCatalogEntry[]
) {
  const templates: RosterTemplateCatalogEntry[] = [];
  const expansions: RosterTemplateCatalogEntry[] = [];
  for (const entry of entries) {
    if (entry.attachOnly) expansions.push(entry);
    else templates.push(entry);
  }
  return { templates, expansions };
}

/**
 * Split a list into the org's own saved templates and the built-in catalog, so
 * the sidebar can lead with what the organization authored.
 */
export function partitionBySource(entries: RosterTemplateCatalogEntry[]) {
  const organization: RosterTemplateCatalogEntry[] = [];
  const builtIn: RosterTemplateCatalogEntry[] = [];
  for (const entry of entries) {
    if (isOrgTemplate(entry)) organization.push(entry);
    else builtIn.push(entry);
  }
  return { organization, builtIn };
}

/**
 * Drop the FEMA EOC template and its custom seats when the briefing flag is
 * off.
 */
export function filterRosterCatalogForFemaOps<
  T extends Pick<
    RosterTemplatesResponse,
    'templates' | 'addablePositionTitles'
  >,
>(catalog: T, femaOps: boolean): T {
  if (femaOps) return catalog;
  const hidden = new Set<string>(FEMA_EOC_CUSTOM_TITLES);
  return {
    ...catalog,
    templates: catalog.templates.filter(
      (entry) => entry.slug !== FEMA_EOC_TEMPLATE_SLUG
    ),
    addablePositionTitles: catalog.addablePositionTitles.filter(
      (title) => !hidden.has(title)
    ),
  };
}
